import { useThree } from '@react-three/fiber'
import {
  extractPointMetadata,
  PointMetadata,
} from 'pages/pointClouds/utils/pointMetadataExtractor'
import { useMemo, useRef } from 'react'
import { Intersection, Object3D, Points, Raycaster, Vector3 } from 'three'

const MAX_DISTANCE = 16
interface BestHit {
  point: Vector3
  distance: number
  object: Object3D
  pointMetadata?: PointMetadata | null
}

export function usePointDetection(enabled: boolean) {
  const { camera, scene, mouse } = useThree()
  const customRaycaster = useRef(new Raycaster())
  customRaycaster.current.params.Points.threshold = 0.05

  const rayDirection = useMemo(() => new Vector3(), [])
  const laserPosition = useMemo(() => new Vector3(), [])

  const detectPoints = () => {
    if (!enabled) {
      return {
        cursorPosition: null,
        bestHit: null,
      }
    }

    rayDirection
      .set(mouse.x, mouse.y, 0.5)
      .unproject(camera)
      .sub(camera.position)
      .normalize()

    const distance = 3
    laserPosition.copy(camera.position).addScaledVector(rayDirection, distance)

    customRaycaster.current.set(camera.position, rayDirection)

    const hits: BestHit[] = []

    scene.traverse((object) => {
      if (object.type !== 'Points' && !object.name.includes('PointCloud')) {
        return
      }

      const pointCloud = object as Points
      const intersects = customRaycaster.current.intersectObject(
        pointCloud,
        true
      ) as Array<Intersection<Points>>

      intersects.forEach((intersect) => {
        if (!(intersect.point instanceof Vector3)) {
          return
        }

        const distanceToCamera = intersect.point.distanceTo(camera.position)
        if (distanceToCamera > MAX_DISTANCE) {
          return
        }

        // Extract color data from the intersection
        let pointMetadata: PointMetadata | null = null
        try {
          pointMetadata = extractPointMetadata(intersect)
        } catch {
          // Ignore errors when extracting point metadata
        }

        hits.push({
          point: intersect.point.clone(),
          object: intersect.object,
          distance: distanceToCamera,
          pointMetadata,
        })
      })
    })

    const bestHit = hits.reduce<BestHit | null>((best, hit) => {
      if (!best || hit.distance < best.distance) {
        return hit
      }
      return best
    }, null)

    return {
      cursorPosition: laserPosition.clone(),
      bestHit,
    }
  }

  return {
    detectPoints,
  }
}
